/**
 * Drives the full-screen overlay in snippets/furlouve-page-loader.liquid.
 * Classic (non-module) script, loaded synchronously right after the
 * overlay markup, so it can hide it as soon as possible — a module
 * script would be deferred and risk the overlay outliving its purpose.
 *
 * Two responsibilities:
 *  1. Fade the overlay out once THIS page has finished loading.
 *  2. Fade it back in just before the browser navigates to another
 *     page on this site, so the transition between pages feels
 *     continuous instead of a bare white flash.
 *
 * Safety net: hidden on a hard timeout regardless of the load event,
 * so a slow-loading resource can never leave visitors stuck behind an
 * opaque overlay.
 *
 * Belt-and-suspenders click guard: the CSS fade (opacity/visibility, see
 * the stylesheet below) is what's *supposed* to take this out of hit-
 * testing, but relying on that transition alone left the overlay as a
 * full-viewport, click-intercepting layer indefinitely in the wild —
 * `visibility: hidden` never took effect even though the `--hidden` class
 * was confirmed present. So once the fade has had time to finish, we also
 * pull the overlay out of layout entirely via inline `display: none`,
 * which cannot be blocked by a stuck transition and beats the
 * stylesheet's `display: flex` on specificity.
 */
(function () {
  var maybeLoader = document.getElementById('FlvPageLoader');
  if (!maybeLoader) return;
  var loader = /** @type {HTMLElement} */ (maybeLoader);

  var HIDE_CLASS = 'flv-page-loader--hidden';
  // Comfortably past the CSS transition above (0.25s, or 0.01s under
  // prefers-reduced-motion) so the fade is always visually done first.
  var REMOVE_FROM_LAYOUT_MS = 300;
  var hidden = false;
  var removeFromLayoutTimer = null;

  function hide() {
    if (hidden) return;
    hidden = true;
    window.clearTimeout(removeFromLayoutTimer);
    loader.classList.add(HIDE_CLASS);
    removeFromLayoutTimer = window.setTimeout(function () {
      loader.style.display = 'none';
    }, REMOVE_FROM_LAYOUT_MS);
  }

  function show() {
    hidden = false;
    window.clearTimeout(removeFromLayoutTimer);
    loader.style.display = '';
    loader.classList.remove(HIDE_CLASS);
  }

  // DOMContentLoaded (not `load`) on purpose: `load` waits on every
  // subresource including slow/flaky third-party scripts (analytics,
  // chat widgets), which can stall for seconds and has nothing to do
  // with whether this page's own content is ready to look at.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hide, { once: true });
  } else {
    hide();
  }
  // Absolute fallback so a stalled resource can never trap visitors
  // behind the overlay.
  setTimeout(hide, 4000);

  // Restored-from-cache navigations (back/forward) don't refire `load`.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) hide();
  });

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var closest = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!closest) return;
    var link = /** @type {HTMLAnchorElement} */ (closest);
    if (link.target === '_blank' || link.hasAttribute('download')) return;

    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;

    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return;
    }

    if (url.origin !== window.location.origin) return;
    // Same-page anchor jump or identical URL — no real navigation happening.
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

    show();
  });
})();
